import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, MapPin } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";

const empty = { title: "", location: "", work: "", description: "", cover: "", before: "", after: "", order: 0 };

export const ProjectsManager = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {id..} = edit
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/projects");
      setProjects(res.data);
    } catch (e) {
      toast.error("Kon projecten niet laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty, order: projects.length }); setEditing("new"); };
  const openEdit = (p) => { setForm(p); setEditing(p.id); };
  const close = () => { setEditing(null); setForm(empty); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.location) { toast.error("Titel en locatie zijn verplicht"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/projects", form);
        toast.success("Project toegevoegd");
      } else {
        await api.put(`/admin/projects/${editing}`, form);
        toast.success("Project bijgewerkt");
      }
      close();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Dit project verwijderen?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Project verwijderd");
    load();
  };

  if (loading) return <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Laden...</div>;

  return (
    <div data-testid="projects-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold text-white">Projecten ({projects.length})</h2>
        <button onClick={openNew} data-testid="add-project-button" className="bg-[#FF1A1A] text-white px-5 py-2.5 font-semibold flex items-center gap-2 hover:bg-[#E60000] transition-colors">
          <Plus size={18} /> Nieuw project
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {projects.map((p) => (
          <div key={p.id} data-testid={`admin-project-${p.id}`} className="bg-[#111] border border-white/10 overflow-hidden flex">
            {p.cover ? (
              <img src={resolveImg(p.cover)} alt="" className="w-32 h-32 object-cover shrink-0" />
            ) : (
              <div className="w-32 h-32 bg-[#0a0a0a] shrink-0" />
            )}
            <div className="p-4 flex-1 min-w-0">
              <div className="flex items-center gap-1 text-[#FF1A1A] text-xs"><MapPin size={12} /> {p.location}</div>
              <h3 className="font-heading text-white font-semibold truncate mt-1">{p.title}</h3>
              <p className="text-white/40 text-xs mt-1 line-clamp-2">{p.work}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => openEdit(p)} data-testid={`edit-project-${p.id}`} className="text-white/60 hover:text-white flex items-center gap-1 text-xs border border-white/15 px-3 py-1.5"><Pencil size={13} /> Bewerk</button>
                <button onClick={() => remove(p.id)} data-testid={`delete-project-${p.id}`} className="text-white/60 hover:text-[#FF1A1A] flex items-center gap-1 text-xs border border-white/15 px-3 py-1.5"><Trash2 size={13} /> Verwijder</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto py-10 px-4">
          <form onSubmit={save} data-testid="project-form" className="bg-[#111] border border-white/10 w-full max-w-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold text-white">{editing === "new" ? "Nieuw project" : "Project bewerken"}</h3>
              <button type="button" onClick={close}><X className="text-white/50 hover:text-white" /></button>
            </div>
            <div className="flex flex-col gap-5">
              <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Titel" data-testid="project-title-input" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-2.5 text-white placeholder:text-white/30" />
              <input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Locatie" data-testid="project-location-input" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-2.5 text-white placeholder:text-white/30" />
              <input value={form.work} onChange={(e) => set("work", e.target.value)} placeholder="Werkzaamheden (bv. PVC vloer, schilderwerk)" data-testid="project-work-input" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-2.5 text-white placeholder:text-white/30" />
              <textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Omschrijving" rows={4} data-testid="project-description-input" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-2.5 text-white placeholder:text-white/30 resize-none" />
              <ImageUpload label="Hoofdfoto" value={form.cover} onChange={(v) => set("cover", v)} testid="project-cover-upload" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <ImageUpload label="Voor-foto" value={form.before} onChange={(v) => set("before", v)} testid="project-before-upload" />
                <ImageUpload label="Na-foto" value={form.after} onChange={(v) => set("after", v)} testid="project-after-upload" />
              </div>
              <button type="submit" disabled={saving} data-testid="save-project-button" className="bg-[#FF1A1A] text-white px-8 py-3.5 font-semibold hover:bg-[#E60000] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {saving ? <><Loader2 size={18} className="animate-spin" /> Opslaan...</> : "Opslaan"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ProjectsManager;
